import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	fetchCustomerByIdQueryOptions,
	useUpdateCustomer,
} from "@/api/customers";
import { useApiParameters } from "@/hooks/useApiParameters";
import {
	CustomerFormPage,
	type CustomerFormValues,
} from "@/routes/_auth/app/_app/org/$orgId/customers/-components/CustomerFormPage";
import { toUpdateCustomerRequest } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/customerFormPayload";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/$customerId/edit",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, customerId } = params;

		// Preload customer data
		context.queryClient.ensureQueryData(
			fetchCustomerByIdQueryOptions(userId, token, orgId, customerId),
		);

		return { userId, token, orgId, customerId };
	},
	component: EditCustomerPage,
});

function EditCustomerPage() {
	const { orgId, customerId } = Route.useParams();
	const { userId, token } = Route.useLoaderData();
	const navigate = useNavigate();

	const { data: customer } = useSuspenseQuery(
		fetchCustomerByIdQueryOptions(userId, token, orgId, customerId),
	);

	const updateCustomerMutation = useUpdateCustomer();

	const handleSubmit = async (values: CustomerFormValues) => {
		try {
			await updateCustomerMutation.mutateAsync({
				token,
				organizationId: orgId,
				customerId,
				data: toUpdateCustomerRequest(values),
			});

			navigate({
				to: "/app/org/$orgId/customers/$customerId",
				params: { orgId, customerId },
			});
		} catch (error) {
			// The mutation hook raises the error toast; staying on the form with the
			// entered values intact is the only thing left to do here.
			console.error("Error updating customer:", error);
		}
	};

	const handleCancel = () =>
		navigate({
			to: "/app/org/$orgId/customers/$customerId",
			params: { orgId, customerId },
		});

	return (
		<div className="h-full min-h-0">
			{/* Keyed by customer id: the form seeds its state from `customer` once at
			    mount, so switching records must remount rather than re-prefill. */}
			<CustomerFormPage
				key={customer.id}
				customer={customer}
				isSubmitting={updateCustomerMutation.isPending}
				onSubmit={handleSubmit}
				onCancel={handleCancel}
			/>
		</div>
	);
}
